"use client";

import { motion, useMotionValue, useTransform } from "framer-motion";
import { MouseEvent } from "react";
import Link from "next/link";
import Magnetic from "./ui/Magnetic";

interface Article {
  title: string;
  category: string;
  date: string;
  readTime: string;
  description: string;
  url: string;
}

const articles: Article[] = [
  {
    title: "Deep-Dive: Node.js Memory Leaks and V8 Heap Profiling",
    category: "Performance",
    date: "July 24, 2026",
    readTime: "8 min read",
    description: "Diagnosing garbage collection anomalies, tracing event listener allocation roots, and executing Chrome DevTools snapshots inside running production containers.",
    url: "/blog/node-memory-leaks"
  },
  {
    title: "System Design: Scaling WebSockets using Redis Pub/Sub Backplanes",
    category: "Architecture",
    date: "May 18, 2026",
    readTime: "12 min read",
    description: "Resolving stateless proxy route boundaries and coordinating horizontal client notification broadcasts across distinct WebSocket application instances.",
    url: "/blog/websocket-scaling"
  },
  {
    title: "DevOps: Zero-Downtime Container Deployments via Traefik Ingress Routing",
    category: "DevOps",
    date: "March 05, 2026",
    readTime: "6 min read",
    description: "Configuring rolling health endpoints, injecting environment keys, and hot-swapping running Docker nodes without dropping active HTTP sessions.",
    url: "/blog/traefik-deployments"
  }
];

function BlogCard({ article, index }: { article: Article; index: number }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
      className="group relative overflow-hidden rounded-[2.5rem] border border-white/5 bg-gradient-to-br from-white/[0.02] to-transparent p-8 hover:border-indigo-500/30 transition-all duration-500 shadow-2xl flex flex-col justify-between"
    >
      {/* Glow spotlight effect */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2.5rem] opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useTransform(
            [mouseX, mouseY],
            ([x, y]) => `radial-gradient(400px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.08), transparent 85%)`
          ),
        }}
      />

      <div className="relative z-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <span className="text-[9px] px-2.5 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-bold uppercase tracking-wider font-mono">
            {article.category}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">{article.readTime}</span>
        </div>

        <Link href={article.url}>
          <h3 className="text-xl font-bold text-white mb-4 leading-snug group-hover:text-indigo-400 transition-colors duration-300 font-[var(--font-poppins)] tracking-tight cursor-pointer">
            {article.title}
          </h3>
        </Link>

        <p className="text-gray-400 text-sm leading-relaxed mb-6 font-medium">
          {article.description}
        </p>
      </div>

      <div className="flex items-center justify-between border-t border-white/5 pt-6 mt-6 relative z-10">
        <span className="text-[10px] text-gray-500 font-mono">{article.date}</span>
        <Magnetic>
          <Link
            href={article.url}
            className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-indigo-400 hover:text-white transition-colors cursor-pointer"
          >
            <span>Read Article</span>
            <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </Magnetic>
      </div>
    </motion.div>
  );
}

export default function Blog() {
  return (
    <section id="blog" className="section-pad relative z-10 bg-black/20 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block font-mono">Publications</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white font-[var(--font-poppins)] tracking-tighter">
            Technical <span className="gradient-text">Writing</span>
          </h2>
          <p className="mt-6 text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Publishing architectural engineering strategies and systems configurations optimizations guidelines.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {articles.map((article, index) => (
            <BlogCard key={article.title} article={article} index={index} />
          ))}
        </div>

        {/* View All CTA */}
        <div className="flex justify-center relative z-10">
          <Magnetic>
            <Link
              href="/blog"
              className="btn-gradient px-8 py-4 rounded-[1.5rem] text-[11px] font-black uppercase tracking-[0.2em] shadow-2xl shadow-indigo-500/20 text-white"
            >
              View Technical Archives
            </Link>
          </Magnetic>
        </div>
      </div>
    </section>
  );
}
